import { cached, geoKey } from "../lib/cache";
import type { Observation } from "../core/signals";
import { dedupePlaces, firstHit, type Hit } from "../core/query";
import { searchOsm } from "./osm";

export interface Place {
  name: string;
  lat: number;
  lon: number;
  /**
   * id записи в geonames, если место пришло из геокодинга Open-Meteo. Нужен,
   * чтобы переразрешить имя на другом языке ТЕМ ЖЕ источником, который его и
   * дал: обратный геокодер на этих же координатах отдаёт «Municipiul
   * Chisinau» вместо «Кишинёв» и «United Kingdom of Great Britain and
   * Northern Ireland» вместо «Британия» (замерено 2026-09-15).
   */
  id?: number;
  /** На каком языке записано name — чтобы видеть, что оно устарело. */
  nameLang?: string;
}

const FIELDS = [
  "current=temperature_2m,apparent_temperature,is_day,weather_code,cloud_cover,precipitation,wind_speed_10m,wind_gusts_10m,wind_direction_10m,pressure_msl",
  "hourly=pressure_msl,precipitation_probability",
  "daily=weather_code,temperature_2m_mean,sunset",
  "past_days=7&forecast_days=2&timezone=auto",
].join("&");

/**
 * Open-Meteo, прямо из браузера: ключа нет, CORS открыт. Данные CC BY 4.0.
 *
 * Держим ответ 10 минут: погода так часто не меняется, а каждое обращение —
 * это чужие бесплатные мощности. Ключ по сетке ~1 км, чтобы соседние точки
 * не били в API по отдельности.
 */
export async function fetchWeather(p: Place): Promise<Observation> {
  return cached(`wx:${geoKey(p.lat, p.lon)}`, 10 * 60 * 1000, () => loadWeather(p));
}

async function loadWeather(p: Place): Promise<Observation> {
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${p.lat}&longitude=${p.lon}&${FIELDS}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Open-Meteo ответил ${r.status}`);
  const d = await r.json();

  const c = d.current;
  const todayIdx = d.daily.time.findIndex((t: string) => t === c.time.slice(0, 10));
  const cut = todayIdx >= 0 ? todayIdx + 1 : d.daily.time.length - 1;

  // Давление за последние 6 часов и вероятность осадков на ближайшие
  const hIdx = d.hourly.time.findIndex((t: string) => t >= c.time);
  const at = hIdx >= 0 ? hIdx : 0;
  const pressureTrend = d.hourly.pressure_msl.slice(Math.max(0, at - 6), at + 1).filter((v: number | null) => v != null);
  const precipSoon = d.hourly.precipitation_probability.slice(at, at + 4).map((v: number | null) => v ?? 0);

  return {
    temp: c.temperature_2m,
    apparent: c.apparent_temperature,
    isDay: !!c.is_day,
    code: c.weather_code,
    cloud: c.cloud_cover,
    precip: c.precipitation ?? 0,
    wind: c.wind_speed_10m ?? 0,
    gusts: c.wind_gusts_10m ?? 0,
    windDir: c.wind_direction_10m ?? undefined,
    pressure: c.pressure_msl ?? 1013,
    now: c.time,
    daily: {
      time: d.daily.time.slice(0, cut),
      code: d.daily.weather_code.slice(0, cut),
      tmean: d.daily.temperature_2m_mean.slice(0, cut),
    },
    sunset: todayIdx >= 0 ? d.daily.sunset[todayIdx] : undefined,
    pressureTrend,
    precipSoon,
  };
}

/** Геокодинг Open-Meteo: локализованные названия, без ключа. Города не переезжают. */
export async function geocode(q: string, lang = "ru"): Promise<Place[]> {
  return cached(`geo:${lang}:${q.toLowerCase().trim()}`, 7 * 864e5, () => loadGeocode(q, lang));
}

/**
 * Поиск с нечёткостью на нашей стороне: геокодер её не умеет (см. query.ts).
 * Лестница обрывается на первой непустой ступени, поэтому обычный запрос
 * стоит ровно один вызов — как и раньше.
 */
export async function searchPlaces(q: string, lang = "ru"): Promise<Hit<Place>> {
  const r = await firstHit(q, (v) => geocode(v, lang));
  if (r.hits.length) return r;

  // Последняя ступень — OpenStreetMap. Он знает сокращения («ЕКБ», «мск»)
  // и прощает опечатку в начале слова («лисабон»), чего префиксный матч
  // Open-Meteo не умеет в принципе. Сюда доходит только то, на чём вся
  // лестница вернула пусто, — и только по явному действию человека.
  try {
    const osm = dedupePlaces(await searchOsm(q, lang));
    if (osm.length) return { query: q, hits: osm, exact: false };
  } catch {
    // OSM лежит — это не повод превращать «не найден» в «геокодинг недоступен».
  }
  return r;
}

async function loadGeocode(q: string, lang: string): Promise<Place[]> {
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=5&language=${lang}&format=json`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Геокодинг ответил ${r.status}`);
  const d = await r.json();
  return (d.results ?? []).map((x: GeoRow) => ({
    name: placeName(x),
    lat: x.latitude,
    lon: x.longitude,
    id: x.id,
    nameLang: lang,
  }));
}

interface GeoRow { id?: number; name: string; country?: string; latitude: number; longitude: number }

const placeName = (x: { name: string; country?: string }) =>
  x.country ? `${x.name}, ${x.country}` : x.name;

/**
 * Имя того же места на другом языке. Держим неделю, как и поиск: города не
 * переезжают и не переименовываются к вечеру.
 */
export async function localizeById(id: number, lang: string): Promise<string | null> {
  return cached(`geoid:${lang}:${id}`, 7 * 864e5, () => loadById(id, lang));
}

async function loadById(id: number, lang: string): Promise<string | null> {
  const r = await fetch(`https://geocoding-api.open-meteo.com/v1/get?id=${id}&language=${lang}&format=json`);
  if (!r.ok) throw new Error(`Геокодинг ответил ${r.status}`);
  const d = (await r.json()) as GeoRow | null;
  return d?.name ? placeName(d) : null;
}

/**
 * Координаты → название города. Open-Meteo обратный геокодинг не умеет, поэтому
 * берём BigDataCloud: без ключа, CORS открыт, названия локализованы.
 *
 * Проверено 2026-09-13 на шести точках: Кишинёв, Ереван, Лондон, Рейкьявик,
 * Токио и село под Кишинёвом (последнее отдаёт район, а не выдуманный город).
 * `api.bigdatacloud.net` отвечает 307 на `api-bdc.io`, поэтому целимся сразу
 * туда и оставляем старый хост запасным.
 */
export async function reverseGeocode(lat: number, lon: number, lang = "ru"): Promise<string | null> {
  return cached(`rev:${lang}:${geoKey(lat, lon)}`, 7 * 864e5, () => loadReverse(lat, lon, lang));
}

async function loadReverse(lat: number, lon: number, lang: string): Promise<string | null> {
  const path = `/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=${lang}`;
  for (const host of ["api-bdc.io", "api.bigdatacloud.net"]) {
    try {
      const r = await fetch(`https://${host}${path}`);
      if (!r.ok) continue;
      const d = await r.json();
      const name = d.city || d.locality || d.principalSubdivision;
      if (name) return d.countryName && d.countryName !== name ? `${name}, ${d.countryName}` : name;
    } catch { /* пробуем следующий хост */ }
  }
  return null;
}
